import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Backdrop,
  Box,
  CircularProgress,
  TablePagination,
  Typography,
} from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import SortableTableHeaderCell, {
  Order,
  orderByTextAriaDescription,
} from "components/SortableTableHeaderCell";
import { tablePaginationSxProps } from "components/Tables";
import { usePageTitle } from "components/PageTitle";
import A11yStatusMessage from "components/A11yStatusMessage";
import { StudyInfo } from "generated/studyauth/studyauth_pb";
import useAllStudiesDataLoader from "./AllStudiesDataLoader";
import { Button, Snackbar, Tag } from "@verily-src/react-design-system";
import { format } from "date-fns";
import { useAuth0 } from "@auth0/auth0-react";
import { useAppSelector } from "redux/hooks";
import { StudyAuthServiceApiClient } from "apiclient/StudyAuthServiceApiClient";
import { telemetry, userNotAuthenticated } from "core/TelemetryController";

interface AllStudiesTableHeaderProps {
  order: Order;
  orderBy: string;
  onRequestSort: (orderBy: string) => void;
}

const AllStudiesTableHeader: React.FC<AllStudiesTableHeaderProps> = ({
  order,
  orderBy,
  onRequestSort,
}) => {
  return (
    <TableHead>
      <TableRow>
        <SortableTableHeaderCell
          columnName="Study name"
          order={order}
          orderBy={orderBy}
          orderByAriaLabel="Study name"
          orderByAriaDescription={orderByTextAriaDescription}
          onRequestSort={onRequestSort}
          width="20%"
          sx={{ paddingLeft: "16px" }}
        >
          <Typography variant="body2em">Study name</Typography>
        </SortableTableHeaderCell>
        <SortableTableHeaderCell
          columnName="Registry ID"
          order={order}
          orderBy={orderBy}
          orderByAriaLabel="Registry ID"
          orderByAriaDescription={orderByTextAriaDescription}
          onRequestSort={onRequestSort}
          width="30%"
          sx={{ paddingLeft: "16px" }}
        >
          <Typography variant="body2em">Registry ID</Typography>
        </SortableTableHeaderCell>
        <SortableTableHeaderCell
          columnName="BigQuery project ID"
          order={order}
          orderBy={orderBy}
          orderByAriaLabel="BigQuery project ID"
          orderByAriaDescription={orderByTextAriaDescription}
          onRequestSort={onRequestSort}
          width="25%"
          sx={{ paddingLeft: "16px" }}
        >
          <Typography variant="body2em">BigQuery project ID</Typography>
        </SortableTableHeaderCell>
        <SortableTableHeaderCell
          columnName="BigQuery dataset"
          order={order}
          orderBy={orderBy}
          orderByAriaLabel="BigQuery dataset"
          orderByAriaDescription={orderByTextAriaDescription}
          onRequestSort={onRequestSort}
          width="25%"
          sx={{ paddingLeft: "16px" }}
        >
          <Typography variant="body2em">BigQuery dataset</Typography>
        </SortableTableHeaderCell>
      </TableRow>
    </TableHead>
  );
};

// Define a component to render study information.
interface StudyRowProps {
  studyInfo: StudyInfo;
}

const StudyRow: React.FC<StudyRowProps> = ({ studyInfo }) => {
  return (
    <TableRow hover>
      <TableCell width="20%" sx={{ paddingLeft: "16px" }}>
        {studyInfo.getDisplayName()}
      </TableCell>
      <TableCell
        width="30%"
        sx={{ paddingLeft: "16px", fontFamily: "Roboto Mono" }}
      >
        {studyInfo.getRegistryId()}
      </TableCell>
      <TableCell width="25%" sx={{ paddingLeft: "16px" }}>
        {studyInfo.getSensorSuiteBqProjectId()}
      </TableCell>
      <TableCell width="25%" sx={{ paddingLeft: "16px" }}>
        {studyInfo.getBqDatasetName()}
      </TableCell>
    </TableRow>
  );
};

interface AllStudiesTableContentProps {
  studies: StudyInfo[];
}

const AllStudiesTableContent: React.FC<AllStudiesTableContentProps> = ({
  studies,
}) => {
  return (
    <TableBody>
      {studies.map((studyInfo) => (
        <StudyRow key={studyInfo.getRegistryId()} studyInfo={studyInfo} />
      ))}
    </TableBody>
  );
};

const AllStudies: React.FC = () => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const auth0Config = useAppSelector((state) => state.auth0Config);

  const { isLoading, hasError, studies, cachedAt, loadAllStudies } =
    useAllStudiesDataLoader();

  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState("Study name");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);

  const [sortedRecords, setSortedRecords] = useState(new Array<StudyInfo>());

  // Status message about the direction of sort for a11y support.
  const [sortDirectionMessage, setSortDirectionMessage] = useState("");

  const handleRequestSort = (orderByColumn: string) => {
    const isAsc = orderBy === orderByColumn && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    setOrder(newOrder);
    setOrderBy(orderByColumn);
    setSortDirectionMessage(
      (isAsc ? "Sort descending on " : "Sort ascending on ") + orderByColumn
    );
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    loadAllStudies();
  }, [loadAllStudies]);

  // Calculate sorted records.
  useEffect(() => {
    const records = getSortedRecords(studies, orderBy, order);
    setSortedRecords(records);
  }, [order, orderBy, studies]);

  // Manage snackbar message
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isErrorMessage, setIsErrorMessage] = useState(false);

  const showErrorMessage = useCallback((message) => {
    setSnackbarMessage(message);
    setIsErrorMessage(true);
    setShowSnackbar(true);
  }, []);

  const showSuccessMessage = useCallback(() => {
    setSnackbarMessage("Data refreshed");
    setIsErrorMessage(false);
    setShowSnackbar(true);
  }, []);

  const [showProcessSpinner, setShowProgressSpinner] = useState(false);

  const handleClickRefreshButton = useCallback(() => {
    if (isAuthenticated && auth0Config) {
      (async () => {
        setShowProgressSpinner(true);

        const token = await getAccessTokenSilently({
          audience: auth0Config.audience,
        });

        const client = new StudyAuthServiceApiClient(
          auth0Config.audience!,
          token
        );

        try {
          await client.refreshCache();

          // Reload studies
          loadAllStudies();

          showSuccessMessage();
        } catch (error) {
          telemetry.logError(error);
          showErrorMessage("Failed to refresh data. Please try again.");
        }

        setShowProgressSpinner(false);
        setFocusOnRefreshDataButton(true);
      })();
    } else {
      telemetry.logError(new Error(userNotAuthenticated));
      showErrorMessage(
        "User is not authenticated. Please logout and login again."
      );
    }
  }, [
    auth0Config,
    getAccessTokenSilently,
    isAuthenticated,
    loadAllStudies,
    showErrorMessage,
    showSuccessMessage,
  ]);

  const refreshDataButtonRef = useRef<HTMLButtonElement | null>(null);
  const [focusOnRefreshDataButton, setFocusOnRefreshDataButton] =
    useState(false);

  // Set page title.
  usePageTitle("All studies");

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%",
          justifyContent: "start",
          alignItems: "start",
          padding: "24px 40px 24px 40px",
        }}
      >
        <Typography variant="display5" component="h1">
          Studies
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "start",
            alignItems: "left",
            width: "100%",
            marginTop: "32px",
          }}
        >
          <A11yStatusMessage message={sortDirectionMessage} />
          <Box
            sx={{
              display: "flex",
              direction: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Tag
              color="primary"
              label={`Last updated ${format(
                cachedAt,
                // date format example: 1/1/2024 3:30pm
                "M/d/yyyy h:mmaaaaa'm'"
              )}`}
            />
            <Button
              label="Refresh data"
              ref={refreshDataButtonRef}
              variant="filled"
              loading={showProcessSpinner}
              disabled={showProcessSpinner || isLoading}
              onClick={() => {
                setFocusOnRefreshDataButton(false);
                handleClickRefreshButton();
                setFocusOnRefreshDataButton(true);
              }}
              sx={{
                marginRight: "16px",
              }}
            />
          </Box>
          <TableContainer>
            <Table sx={{ minWidth: 700 }} aria-label="Studies table">
              <AllStudiesTableHeader
                order={order}
                orderBy={orderBy}
                onRequestSort={(orderBy) => {
                  handleRequestSort(orderBy);
                }}
              />
              <AllStudiesTableContent
                studies={sortedRecords.slice(
                  page * rowsPerPage,
                  page * rowsPerPage + rowsPerPage
                )}
              />
            </Table>
          </TableContainer>
          {/* Show error message if there is a loading error */}
          {!isLoading && hasError && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "start",
                alignItems: "start",
                marginY: "16px",
                width: "100%",
              }}
            >
              <Alert severity="error">
                Failed to load studies. Please try again.
              </Alert>
            </Box>
          )}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={sortedRecords.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ marginRight: "8px", ...tablePaginationSxProps }}
          />
        </Box>
        <Backdrop
          sx={{
            color: "#fff",
            zIndex: (theme: { zIndex: { drawer: number } }) =>
              theme.zIndex.drawer + 1,
          }}
          open={isLoading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Box>
      <Snackbar
        role="alert"
        text={snackbarMessage}
        color={isErrorMessage ? "error" : "success"}
        withIcon
        open={showSnackbar}
        onClose={() => {
          setShowSnackbar(false);
          if (focusOnRefreshDataButton) {
            setTimeout(() => {
              refreshDataButtonRef.current?.focus();
            }, 200);
          }
        }}
        autoHideDuration={2000}
      />
    </>
  );
};

function getSortedRecords(
  studies: Array<StudyInfo>,
  orderBy: string,
  order: string
) {
  const studyList = studies.sort((a, b) => {
    let result = 0;
    switch (orderBy) {
      case "Registry ID":
        result = a.getRegistryId().localeCompare(b.getRegistryId());
        break;
      case "Study name":
        result = a.getDisplayName().localeCompare(b.getDisplayName());
        break;
      case "BigQuery project ID":
        result = a
          .getSensorSuiteBqProjectId()
          .localeCompare(b.getSensorSuiteBqProjectId());
        break;
      case "BigQuery dataset":
        result = a.getBqDatasetName().localeCompare(b.getBqDatasetName());
        break;
    }
    return order === "asc" ? result : -result;
  });

  return Array.from(studyList);
}

export default AllStudies;
